import { useSeoMeta } from '@unhead/react';
import { TombstoneLayout } from '@/components/TombstoneLayout';
import { RelayListManager } from '@/components/RelayListManager';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { LoginArea } from '@/components/auth/LoginArea';
import { useAppContext } from '@/hooks/useAppContext';
import { useQuery } from '@tanstack/react-query';
import { Wifi, WifiOff, AlertCircle, RefreshCw, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

type ConnStatus = 'connected' | 'disconnected' | 'error';

function useRelayConnectionStatus(relays: string[]) {
  return useQuery({
    queryKey: ['relay-connections', relays.join(',')],
    queryFn: async () => {
      const results: Record<string, ConnStatus> = {};

      await Promise.all(
        relays.map(async (url) => {
          try {
            const ws = new WebSocket(url);

            const result = await new Promise<ConnStatus>((resolve) => {
              const timeout = setTimeout(() => {
                try { ws.close(); } catch { /* ignore */ }
                resolve('disconnected');
              }, 5000);

              ws.onopen = () => {
                clearTimeout(timeout);
                try { ws.close(); } catch { /* ignore */ }
                resolve('connected');
              };

              ws.onerror = () => {
                clearTimeout(timeout);
                resolve('error');
              };
            });

            results[url] = result;
          } catch {
            results[url] = 'error';
          }
        })
      );

      return results;
    },
    staleTime: 30000,
    retry: false,
  });
}

export default function RelaysPage() {
  useSeoMeta({
    title: 'Relay Management — Tombstone',
    description: 'Manage your Nostr relay connections for maximum deletion efficacy.',
  });

  const { user } = useCurrentUser();
  const { config } = useAppContext();
  const relays = config.relayMetadata.relays.map((r) => r.url);
  const { data: connStatus, isFetching, refetch } = useRelayConnectionStatus(relays);

  const statusCounts = connStatus
    ? {
        connected: Object.values(connStatus).filter((s) => s === 'connected').length,
        disconnected: Object.values(connStatus).filter((s) => s === 'disconnected').length,
        error: Object.values(connStatus).filter((s) => s === 'error').length,
      }
    : null;

  return (
    <TombstoneLayout>
      <div className="max-w-3xl mx-auto px-6 py-10 space-y-8">
        {/* Header */}
        <div>
          <h1 className="font-display text-2xl font-semibold text-foreground tracking-tight">
            Relay Management
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Configure the relays Tombstone uses to query and broadcast deletion requests.
          </p>
        </div>

        {/* Info callout */}
        <div className="rounded-lg border border-[hsl(var(--accent))]/20 bg-[hsl(var(--accent))]/5 p-4 flex gap-3">
          <Info className="w-4 h-4 text-[hsl(var(--accent))] flex-shrink-0 mt-0.5" />
          <div className="space-y-1 text-sm">
            <p className="font-medium text-foreground">Why relay management matters for deletion</p>
            <p className="text-muted-foreground leading-relaxed text-xs">
              Not all relays honor NIP-09 deletion requests. To maximize efficacy, add all relays
              where your events may have propagated — including relays you've used in the past.
              Tombstone will broadcast your deletion request to every configured relay with write access.
            </p>
          </div>
        </div>

        {/* Connection status overview */}
        <div className="rounded-lg border border-border bg-card p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-medium text-sm text-foreground">
              Connection Status
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
              className="h-7 px-2 text-xs text-muted-foreground"
            >
              <RefreshCw className={cn('w-3 h-3 mr-1.5', isFetching && 'animate-spin')} />
              Test all
            </Button>
          </div>

          {isFetching ? (
            <div className="space-y-2">
              {relays.map((url) => (
                <Skeleton key={url} className="h-10 rounded" />
              ))}
            </div>
          ) : (
            <>
              {statusCounts && (
                <div className="flex items-center gap-4 text-xs text-muted-foreground pb-1">
                  <span className="flex items-center gap-1.5 text-green-400">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
                    {statusCounts.connected} connected
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground inline-block" />
                    {statusCounts.disconnected} unreachable
                  </span>
                  {statusCounts.error > 0 && (
                    <span className="flex items-center gap-1.5 text-amber-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
                      {statusCounts.error} error
                    </span>
                  )}
                </div>
              )}

              <div className="space-y-2">
                {relays.map((url) => {
                  const relayConfig = config.relayMetadata.relays.find((r) => r.url === url);
                  const status: ConnStatus = connStatus?.[url] ?? 'disconnected';
                  const displayUrl = url.replace(/^wss?:\/\//, '');

                  return (
                    <div
                      key={url}
                      className="flex items-center gap-3 p-3 rounded-lg border border-border bg-secondary/20"
                    >
                      <RelayStatusIcon status={status} />
                      <span className="font-mono text-xs text-foreground flex-1 truncate" title={url}>
                        {displayUrl}
                      </span>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {relayConfig?.read && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded border border-blue-500/20 bg-blue-500/10 text-blue-400">
                            read
                          </span>
                        )}
                        {relayConfig?.write && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded border border-purple-500/20 bg-purple-500/10 text-purple-400">
                            write
                          </span>
                        )}
                        <RelayStatusBadge status={status} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Relay list manager */}
        <div className="rounded-lg border border-border bg-card p-5 space-y-4">
          <div>
            <h2 className="font-display font-medium text-sm text-foreground">
              Configure Relays
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Add or remove relays. Changes are saved locally and optionally published as NIP-65.
            </p>
          </div>

          {user ? (
            <RelayListManager />
          ) : (
            <div className="rounded-lg border border-dashed border-border py-10 text-center space-y-4">
              <p className="text-sm text-muted-foreground">
                Log in to manage and sync your relay list with Nostr.
              </p>
              <LoginArea className="max-w-56 mx-auto" />
            </div>
          )}
        </div>

        {/* NIP-09 note */}
        <div className="rounded-lg border border-border bg-card/50 p-4 space-y-2 text-xs text-muted-foreground">
          <p className="font-medium text-foreground text-sm">About NIP-09 deletion & relay compliance</p>
          <p className="leading-relaxed">
            NIP-09 defines a "deletion request" event (kind 5) that signals to relays and clients
            that an event should be removed. Tombstone broadcasts this to all configured write relays.
          </p>
          <p className="leading-relaxed">
            However, Nostr is permissionless — relays <em>should</em> honor deletion requests per
            the spec, but are not required to. If a relay ignores your request, the "Nullify" option
            (available for replaceable and addressable events) overwrites the event content instead.
          </p>
        </div>
      </div>
    </TombstoneLayout>
  );
}

function RelayStatusIcon({ status }: { status: ConnStatus }) {
  if (status === 'connected') return <Wifi className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />;
  if (status === 'error') return <AlertCircle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />;
  return <WifiOff className="w-3.5 h-3.5 text-muted-foreground/50 flex-shrink-0" />;
}

function RelayStatusBadge({ status }: { status: ConnStatus }) {
  if (status === 'connected') {
    return (
      <span className="text-[10px] px-1.5 py-0.5 rounded border border-green-500/20 bg-green-500/10 text-green-400">
        online
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="text-[10px] px-1.5 py-0.5 rounded border border-amber-500/20 bg-amber-500/10 text-amber-400">
        error
      </span>
    );
  }
  return (
    <span className="text-[10px] px-1.5 py-0.5 rounded border border-border bg-secondary text-muted-foreground">
      offline
    </span>
  );
}
